<template>
  <div class="mt-3 flex w-full flex-col gap-4">
    <div class="flex flex-col gap-1">
      <h3 class="text-base font-bold text-gray-600 capitalize-first">
        {{ $t('attributes.group_by') }}
      </h3>
      <div class="flex flex-row flex-wrap gap-2">
        <BaseButton
          data-test="group-chip"
          :title="$t('global.group_by_none')"
          size="small"
          :is-outline="settings.group.column !== null"
          @click="setGroup(null)"
        />
        <BaseButton
          v-for="column in groupable"
          :key="column.id"
          data-test="group-chip"
          :title="column.name"
          size="small"
          :is-outline="settings.group.column !== column.id"
          @click="setGroup(column.id)"
        />
      </div>
      <div
        v-if="settings.group.column"
        class="flex items-center gap-2 text-sm text-gray-500"
      >
        <span class="capitalize-first">{{ $t('global.group_order') }}</span>
        <BaseButton
          v-for="direction in directions"
          :key="direction.value"
          :title="direction.label"
          :icon="direction.icon[1]"
          :icon-type="direction.icon[0]"
          size="small"
          :is-outline="settings.group.direction !== direction.value"
          inverse-icon-order
          @click="
            emitSettings({
              group: { ...settings.group, direction: direction.value }
            })
          "
        />
      </div>
    </div>
    <div class="flex flex-col gap-1">
      <h3 class="text-base font-bold text-gray-600 capitalize-first">
        {{ $t('global.sort_by') }}
      </h3>
      <div
        v-for="level in sortLevels"
        :key="level"
        class="flex items-center gap-2"
      >
        <span class="w-20 text-sm text-gray-500 capitalize-first">
          {{ level === 0 ? $t('global.sort_by') : $t('global.then_by') }}
        </span>
        <select
          data-test="sort-select"
          class="block rounded-md border-gray-300 text-sm focus:border-blue-500 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-700 dark:text-white"
          :aria-label="
            level === 0 ? $t('global.sort_by') : $t('global.then_by')
          "
          :value="settings.sort[level]?.column ?? ''"
          @change="setSortColumn(level, $event.target.value)"
        >
          <option value="">—</option>
          <option
            v-for="column in sortable"
            :key="column.id"
            :value="column.id"
          >
            {{ column.name }}
          </option>
        </select>
        <BaseButton
          v-for="direction in directions"
          :key="direction.value"
          :title="direction.label"
          :icon="direction.icon[1]"
          :icon-type="direction.icon[0]"
          size="small"
          :is-outline="settings.sort[level]?.direction !== direction.value"
          :disabled="!settings.sort[level]"
          inverse-icon-order
          @click="setSortDirection(level, direction.value)"
        />
      </div>
    </div>
    <div class="flex flex-col gap-1">
      <h3 class="text-base font-bold text-gray-600 capitalize-first">
        {{ $t('global.card_fields') }}
      </h3>
      <div class="flex flex-row flex-wrap gap-2">
        <BaseButton
          v-for="field in cardFields"
          :key="field.key"
          :data-test="'field-chip-' + field.key"
          :title="field.label"
          size="small"
          :is-outline="!settings.fields[field.key]"
          @click="
            emitSettings({
              fields: {
                ...settings.fields,
                [field.key]: !settings.fields[field.key]
              }
            })
          "
        />
      </div>
    </div>
    <div class="grid w-full grid-cols-2 gap-2">
      <div class="flex flex-col gap-x-2 gap-y-1">
        <h3 class="text-base font-bold text-gray-600 capitalize-first">
          {{ $t('global.column_status_to_display') }}
        </h3>
        <div class="flex flex-row flex-wrap gap-2">
          <BaseButton
            v-for="item in task_statuses"
            :key="item.id"
            :data-test="'status-chip-' + item.id"
            :title="$t('enums.task.status.' + item.id)"
            :icon="item.icon"
            :color="item.bg_color"
            size="small"
            :is-outline="!settings.visibleStatuses.includes(item.id)"
            inverse-icon-order
            @click="toggleVisibleStatus(item.id)"
          />
        </div>
        <p class="-mt-1 text-gray-500">
          {{ $t('global.column_status_to_display_info') }}
        </p>
      </div>
      <div class="flex flex-col gap-x-2 gap-y-1">
        <h3 class="text-base font-bold text-gray-600 capitalize-first">
          {{ $t('global.task_status_change') }}
        </h3>
        <div class="flex flex-row flex-wrap gap-2">
          <BaseButton
            v-for="item in statusInteractionOptions"
            :key="item.value"
            :data-test="'interaction-chip-' + item.value"
            :title="item.label"
            :icon="item.icon"
            size="small"
            :is-outline="item.value !== settings.statusInteraction"
            inverse-icon-order
            @click="emitSettings({ statusInteraction: item.value })"
          />
        </div>
      </div>
    </div>
    <div class="flex items-center justify-between">
      <span class="text-xs text-gray-500">
        {{ $t('global.everything_applies_live') }}
      </span>
      <BaseButton
        :title="$t('button.close')"
        size="small"
        @click="$emit('close-popover')"
      />
    </div>
  </div>
</template>

<script>
import BaseButton from '@c/addf-package/components/BaseButton/BaseButton.vue'
import { mapGetters } from 'vuex'
import {
  groupableColumns,
  sortableColumns
} from '../utils/taskDashboardColumns'

export default {
  name: 'DisplayOptionsTaskContent',
  components: { BaseButton },
  props: {
    settings: { type: Object, required: true },
    columns: { type: Array, required: true }
  },
  emits: ['update:settings', 'close-popover'],
  data() {
    return {
      sortLevels: [0, 1],
      directions: [
        {
          label: this.$t('global.asc'),
          icon: ['far', 'arrow-up-a-z'],
          value: 'asc'
        },
        {
          label: this.$t('global.desc'),
          icon: ['far', 'arrow-down-z-a'],
          value: 'desc'
        }
      ],
      cardFields: [
        { key: 'item', label: this.$t('attributes.item') },
        { key: 'due_date', label: this.$t('attributes.due_date') },
        { key: 'created_at', label: this.$t('attributes.creation_date') },
        { key: 'funeral_date', label: this.$t('attributes.burial_date') },
        { key: 'funeral_home', label: this.$t('attributes.funeral_home') },
        { key: 'company', label: this.$t('attributes.company') },
        { key: 'responsable', label: this.$t('attributes.responsable') },
        { key: 'assigned', label: this.$t('global.assignedPeople') }
      ],
      statusInteractionOptions: [
        {
          value: 'cycle',
          label: this.$t('global.in_one_click'),
          icon: 'arrow-pointer'
        },
        {
          value: 'select',
          label: this.$t('global.select'),
          icon: 'list'
        }
      ]
    }
  },
  computed: {
    ...mapGetters({
      task_statuses: 'metadata/getMetadataTaskStatuses'
    }),
    groupable() {
      return groupableColumns(this.columns)
    },
    sortable() {
      return sortableColumns(this.columns)
    }
  },
  methods: {
    emitSettings(patch) {
      this.$emit('update:settings', { ...this.settings, ...patch })
    },
    setGroup(column) {
      this.emitSettings({
        group: { column, direction: this.settings.group.direction }
      })
    },
    setSortColumn(level, column) {
      const sort = [...this.settings.sort]
      if (column === '') {
        sort.splice(level, 1)
      } else {
        sort[level] = { column, direction: sort[level]?.direction ?? 'asc' }
      }
      this.emitSettings({ sort: sort.filter(Boolean).slice(0, 2) })
    },
    setSortDirection(level, direction) {
      this.emitSettings({
        sort: this.settings.sort.map((entry, index) =>
          index === level ? { ...entry, direction } : entry
        )
      })
    },
    toggleVisibleStatus(statusId) {
      const visibleStatuses = this.settings.visibleStatuses.includes(statusId)
        ? this.settings.visibleStatuses.filter((id) => id !== statusId)
        : [...this.settings.visibleStatuses, statusId]
      this.emitSettings({ visibleStatuses })
    }
  }
}
</script>
